import { useState } from 'react';
import { AlertCircle, PackagePlus, Save } from 'lucide-react';
import type { Product } from '../../../shared/types';
import { pb, errorText } from '../lib/pb';
import CreatorModal from './CreatorModal';
import FieldHint from './FieldHint';
import ImageLinkCheck, { type ImageCheckResult } from './ImageLinkCheck';

/**
 * Create — or, given `existing`, edit — a product without touching the Admin
 * tab. Editing is open to the product's creator and admins (the server
 * enforces the same rule). The fields mirror what the pipeline actually uses:
 * benefits feed the script writer, the photo feeds the image model (it matches
 * the bottle's shape and label), guidelines are extra script rules. The photo
 * link is verified live — it must actually load before the product can be
 * saved, which catches Drive folder links and not-publicly-shared files.
 */

function slugify(text: string): string {
  return text
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
}

function looksLikeUrl(value: string): boolean {
  return /^https?:\/\/\S+$/i.test(value.trim());
}

const HINTS = {
  brand: {
    what: 'The brand this product belongs to. Products are grouped by brand in the campaign picker, so spell it exactly like the existing products of the same brand.',
    example: 'NatureBoost',
  },
  name: {
    what: 'The product name as it should be spoken in scripts and shown in the app.',
    example: 'Marine Collagen Peptides',
  },
  link: {
    what: 'The store page. Not used in generation — it lets reviewers check the real product while judging scripts.',
    example: 'https://naturebooststore.com/products/marine-collagen',
  },
  benefits: {
    what: 'The talking points the script writer draws from. Write them as short, concrete, true statements — every AI script is built on these, so vague or exaggerated claims here become vague or exaggerated scripts.',
    example:
      'Supports skin elasticity and hydration. 10g of hydrolyzed marine collagen per serving. Unflavored — dissolves fully in coffee or smoothies. Third-party tested. One simple scoop per day.',
  },
  image: {
    what: 'A clear photo of the product on a plain background. The image model matches the bottle’s shape, size and label from this photo whenever the avatar holds it, so a clean straight-on shot matters. Drive link (to the image file itself, shared as "Anyone with the link") or direct image link. The box on the left shows whether the link really loads.',
    example: 'https://your-bucket.com/products/marine-collagen-front.jpg',
  },
  guidelines: {
    what: 'Product-specific script rules the writer must follow — required phrasing, legal do-not-say list, positioning.',
    example:
      'Always say "supports" rather than "improves". Never promise visible results or mention timeframes. Target audience: women 35+, tone practical not glamorous.',
  },
} as const;

export default function ProductCreator({
  userId,
  existing,
  onClose,
  onCreated,
}: {
  userId: string;
  /** When set, the dialog edits this product instead of creating a new one. */
  existing?: Product;
  onClose: () => void;
  /** Called with the product's name after it is saved. */
  onCreated: (name: string) => void;
}) {
  const editing = Boolean(existing);
  const [brand, setBrand] = useState(existing?.brand ?? '');
  const [name, setName] = useState(existing?.name ?? '');
  const [link, setLink] = useState(existing?.link ?? '');
  const [benefits, setBenefits] = useState(existing?.benefits ?? '');
  const [imageUrl, setImageUrl] = useState(existing?.image_url ?? '');
  const [guidelines, setGuidelines] = useState(existing?.guidelines ?? '');
  const [imageCheck, setImageCheck] = useState<ImageCheckResult>({ ok: false, message: null });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const problems: string[] = [];
  if (!brand.trim()) problems.push('a brand');
  if (!name.trim()) problems.push('a product name');
  if (!benefits.trim()) problems.push('the benefits (the script writer needs them)');
  if (!imageCheck.ok) problems.push('a product photo link that loads (see the preview box)');
  if (link.trim() && !looksLikeUrl(link)) problems.push('a valid store link (must start with http)');

  const canSubmit = problems.length === 0 && !busy;

  const submit = async () => {
    if (!canSubmit) return;
    setBusy(true);
    setError(null);
    try {
      const fields = {
        brand: brand.trim(),
        name: name.trim(),
        link: link.trim(),
        benefits: benefits.trim(),
        image_url: imageUrl.trim(),
        guidelines: guidelines.trim(),
      };
      if (existing) {
        await pb.collection('products').update(existing.id, fields);
      } else {
        await pb.collection('products').create({
          ...fields,
          product_id: `${slugify(`${brand} ${name}`) || 'product'}_${Math.random().toString(36).slice(2, 6)}`,
          created_by: userId,
          active: true,
        });
      }
      onCreated(name.trim());
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <CreatorModal
      title={editing ? `Edit ${existing?.name || 'product'}` : 'Create a new product'}
      subtitle="Hover the ⓘ icons to see what each field does, with a good example."
      onClose={onClose}
    >
      <div className="space-y-5">
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="zen-label">
              Brand *
              <FieldHint {...HINTS.brand} />
            </label>
            <input value={brand} onChange={(e) => setBrand(e.target.value)} className="zen-input" placeholder="e.g. NatureBoost" />
          </div>
          <div>
            <label className="zen-label">
              Product name *
              <FieldHint {...HINTS.name} />
            </label>
            <input value={name} onChange={(e) => setName(e.target.value)} className="zen-input" placeholder="e.g. Marine Collagen Peptides" />
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="zen-label">
              Product photo link *
              <FieldHint {...HINTS.image} />
            </label>
            <div className="flex items-start gap-2">
              <ImageLinkCheck url={imageUrl} alt="Product" onResult={setImageCheck} />
              <input
                value={imageUrl}
                onChange={(e) => setImageUrl(e.target.value)}
                className="zen-input text-xs flex-1"
                placeholder="Drive link or direct image link"
              />
            </div>
            {imageCheck.message && (
              <p className="text-[11px] text-red-500 mt-1.5 flex items-start gap-1">
                <AlertCircle size={11} className="shrink-0 mt-0.5" />
                {imageCheck.message}
              </p>
            )}
          </div>
          <div>
            <label className="zen-label">
              Store link (optional)
              <FieldHint {...HINTS.link} />
            </label>
            <input
              value={link}
              onChange={(e) => setLink(e.target.value)}
              className="zen-input text-xs"
              placeholder="https://…"
            />
          </div>
        </div>

        <div>
          <label className="zen-label">
            Benefits — the script's talking points *
            <FieldHint {...HINTS.benefits} />
          </label>
          <textarea
            value={benefits}
            onChange={(e) => setBenefits(e.target.value)}
            rows={4}
            className="zen-input text-xs leading-relaxed resize-y"
            placeholder="Short, concrete, true statements — one per line works well"
          />
        </div>

        <div>
          <label className="zen-label">
            Script guidelines (optional)
            <FieldHint {...HINTS.guidelines} />
          </label>
          <textarea
            value={guidelines}
            onChange={(e) => setGuidelines(e.target.value)}
            rows={3}
            className="zen-input text-xs leading-relaxed resize-y"
            placeholder="Required phrasing, things never to say, positioning"
          />
        </div>

        {error && (
          <div className="p-3 bg-red-50 text-red-600 border border-red-200 rounded-xl text-xs flex items-start gap-2">
            <AlertCircle size={14} className="shrink-0 mt-0.5" />
            <span>{error}</span>
          </div>
        )}

        {!canSubmit && !busy && problems.length > 0 && (
          <p className="text-[11px] text-natural-text/45">Still needed: {problems.join(', ')}.</p>
        )}

        <button onClick={submit} disabled={!canSubmit} className="zen-btn-primary w-full">
          {editing ? <Save size={15} /> : <PackagePlus size={15} />}
          {busy ? 'Saving…' : editing ? 'Save changes' : 'Create product'}
        </button>
      </div>
    </CreatorModal>
  );
}
